import {useEffect, useMemo, useState} from "react";
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs, {Dayjs} from "dayjs";
import {useTranslation} from "react-i18next";
import {EmployeeUserType} from "../../../types/employeeType";
import {GeneralButton} from "../../../common/ui/GeneralButton";
import {absenceApi} from "../../../api/absenceApi";
import {CreateVacationQuotaRequest, UpdateVacationQuotaRequest, VacationQuota,} from "../../../types/absence";
import {HolidayExtent} from "../../../types/holidayExtent";
import { RegionalHolidays, RegionalHolidaysState } from "./RegionalHolidays";

type WorkdaysState = {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
};

type VacationQuotaDialogProps = {
  open: boolean;
  onClose: () => void;
  employee: EmployeeUserType;
  absenceServiceModuleId?: number;
  canEdit: boolean;
  companyId: number | null;
  mode: "view" | "create";
};

const defaultWorkdays: WorkdaysState = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
};

const defaultRegionalHolidays: RegionalHolidaysState = {
  holy3KingsDay: HolidayExtent.None,
  internationalWomensHoliday: HolidayExtent.None,
  fronleichnamHoliday: HolidayExtent.None,
  mariaHimmelfahrtHoliday: HolidayExtent.None,
  worldChildrenDayHoliday: HolidayExtent.None,
  reformationDayHoliday: HolidayExtent.None,
  allerheiligenDayHoliday: HolidayExtent.None,
  bussUndBetTagHoliday: HolidayExtent.None,
  augsburgerFriedensfestHoliday: HolidayExtent.None,
  christmasEveHoliday: HolidayExtent.None,
  newYearsEveHoliday: HolidayExtent.None,
  easterSundayHoliday: HolidayExtent.None,
  pfingstSundayHoliday: HolidayExtent.None,
};

const VacationQuotaDialog = ({
  open,
  onClose,
  employee,
  absenceServiceModuleId,
  canEdit,
  companyId,
  mode,
}: VacationQuotaDialogProps) => {
  const { t } = useTranslation();
  const [vacationDays, setVacationDays] = useState("");
  const [surplusDays, setSurplusDays] = useState("");
  const [validFrom, setValidFrom] = useState<Dayjs | null>(null);
  const [validUntil, setValidUntil] = useState<Dayjs | null>(null);
  const [surplusValidUntil, setSurplusValidUntil] = useState<Dayjs | null>(null);
  const [workdays, setWorkdays] = useState<WorkdaysState>(defaultWorkdays);
  const [regionalHolidays, setRegionalHolidays] = useState<RegionalHolidaysState>(
    defaultRegionalHolidays
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vacationQuotas, setVacationQuotas] = useState<VacationQuota[]>([]);
  const [vacationQuotaLoading, setVacationQuotaLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editQuotaId, setEditQuotaId] = useState<number | "">("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setVacationDays("");
    setSurplusDays("");
    setValidFrom(null);
    setValidUntil(null);
    setSurplusValidUntil(null);
    setWorkdays(defaultWorkdays);
    setRegionalHolidays(defaultRegionalHolidays);
    setVacationQuotas([]);
    setVacationQuotaLoading(false);
    setIsEditMode(false);
    setEditQuotaId("");
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [open, employee.id, mode]);

  useEffect(() => {
    if (!open || !absenceServiceModuleId) return;
    const fetchQuotas = async () => {
      setVacationQuotaLoading(true);
      const result = await absenceApi.getVacationQuotas(
        absenceServiceModuleId,
        employee.id
      );
      if (result.success() && result.result) {
        setVacationQuotas(result.result);
      } else {
        setVacationQuotas([]);
      }
      setVacationQuotaLoading(false);
    };
    fetchQuotas();
  }, [open, absenceServiceModuleId, employee.id, mode]);

  const activeQuota = useMemo(() => {
    if (!vacationQuotas.length) return null;
    const today = dayjs();
    return (
      vacationQuotas.find((quota) =>
        today.isAfter(dayjs(quota.validFrom).subtract(1, "day")) &&
        today.isBefore(dayjs(quota.validUntil).add(1, "day"))
      ) || null
    );
  }, [vacationQuotas]);

  const nextYearQuota = useMemo(() => {
    if (!activeQuota) return null;
    const nextYear = dayjs(activeQuota.validFrom).year() + 1;
    return (
      vacationQuotas.find((quota) =>
        dayjs(quota.validFrom).year() === nextYear ||
        dayjs(quota.validUntil).year() === nextYear
      ) || null
    );
  }, [activeQuota, vacationQuotas]);

  const isCreateOnly = vacationQuotas.length === 0;

  const toggleWorkday = (key: keyof WorkdaysState) => {
    setWorkdays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!canEdit) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.noPermission"));
      return;
    }

    if (!absenceServiceModuleId) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.missingModule"));
      return;
    }

    if (!isEditMode && companyId === null) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.missingCompany"));
      return;
    }

    if (!validFrom || !validUntil || !dayjs(validUntil).isAfter(validFrom)) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.invalidDateRange"));
      return;
    }

    const totalVacationDays = Number(vacationDays);
    if (vacationDays === "" || Number.isNaN(totalVacationDays) || totalVacationDays < 0) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.invalidVacationDays"));
      return;
    }

    if (!surplusValidUntil) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.invalidSurplusValidUntil"));
      return;
    }

    const parsedSurplusDays = Number(surplusDays);
    if (surplusDays === "" || Number.isNaN(parsedSurplusDays) || parsedSurplusDays < 0) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.invalidSurplusDays"));
      return;
    }

    const dateFrom = validFrom.format("YYYY-MM-DD");
    const dateUntil = validUntil.format("YYYY-MM-DD");

    const updatePayload: UpdateVacationQuotaRequest = {
      employeeId: employee.id,
      validFrom: dateFrom,
      validUntil: dateUntil,
      totalVacationDays,
      surplusFromPrevious: parsedSurplusDays,
      surplusValidUntil: surplusValidUntil.format("YYYY-MM-DD"),
      isMondayWorkday: workdays.monday,
      isTuesdayWorkday: workdays.tuesday,
      isWednesdayWorkday: workdays.wednesday,
      isThursdayWorkday: workdays.thursday,
      isFridayWorkday: workdays.friday,
      isSaturdayWorkday: workdays.saturday,
      isSundayWorkday: workdays.sunday,
      holy3KingsDayHoliday: regionalHolidays.holy3KingsDay,
      internationalWomensHoliday: regionalHolidays.internationalWomensHoliday,
      fronleichnamHoliday: regionalHolidays.fronleichnamHoliday,
      mariaHimmelfahrtHoliday: regionalHolidays.mariaHimmelfahrtHoliday,
      worldChildrenDayHoliday: regionalHolidays.worldChildrenDayHoliday,
      reformationDayHoliday: regionalHolidays.reformationDayHoliday,
      allerheiligenDayHoliday: regionalHolidays.allerheiligenDayHoliday,
      bussUndBetTagHoliday: regionalHolidays.bussUndBetTagHoliday,
      augsburgerFriedensfestHoliday: regionalHolidays.augsburgerFriedensfestHoliday,
      christmasEveHoliday: regionalHolidays.christmasEveHoliday,
      newYearsEveHoliday: regionalHolidays.newYearsEveHoliday,
      easterSundayHoliday: regionalHolidays.easterSundayHoliday,
      pfingstSundayHoliday: regionalHolidays.pfingstSundayHoliday,
    };

    try {
      setIsSubmitting(true);
      if (isEditMode && !editQuotaId) {
        setErrorMessage(t("employeesPage.vacationQuota.errors.noActiveToEdit"));
        return;
      }

      let result;
      if (isEditMode && editQuotaId) {
        result = await absenceApi.updateVacationQuota(
          absenceServiceModuleId,
          Number(editQuotaId),
          updatePayload,
          undefined
        );
      } else {
        const createPayload: CreateVacationQuotaRequest = {
          ...updatePayload,
          companyId: companyId as number,
        };
        result = await absenceApi.createVacationQuota(
          absenceServiceModuleId,
          createPayload
        );
      }
      if (!result.success()) {
        setErrorMessage(
          result.errorMessage ??
            t("employeesPage.vacationQuota.errors.generic")
        );
        return;
      }

      setSuccessMessage(
        isEditMode
          ? t("employeesPage.vacationQuota.successUpdated")
          : t("employeesPage.vacationQuota.success")
      );
      if (isEditMode) {
        onClose();
      }
      setIsEditMode(false);
      setEditQuotaId("");
    } catch (error) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSelection = (quota: VacationQuota) => {
    setVacationDays(String(quota.totalVacationDays));
    setSurplusDays(String(quota.surplusFromPrevious));
    setValidFrom(dayjs(quota.validFrom));
    setValidUntil(dayjs(quota.validUntil));
    setSurplusValidUntil(dayjs(quota.surplusValidUntil));
    setWorkdays({
      monday: quota.isMondayWorkday,
      tuesday: quota.isTuesdayWorkday,
      wednesday: quota.isWednesdayWorkday,
      thursday: quota.isThursdayWorkday,
      friday: quota.isFridayWorkday,
      saturday: quota.isSaturdayWorkday,
      sunday: quota.isSundayWorkday,
    });
    setRegionalHolidays({
      holy3KingsDay: quota.holy3KingsDayHoliday,
      internationalWomensHoliday: quota.internationalWomensHoliday,
      fronleichnamHoliday: quota.fronleichnamHoliday,
      mariaHimmelfahrtHoliday: quota.mariaHimmelfahrtHoliday,
      worldChildrenDayHoliday: quota.worldChildrenDayHoliday,
      reformationDayHoliday: quota.reformationDayHoliday,
      allerheiligenDayHoliday: quota.allerheiligenDayHoliday,
      bussUndBetTagHoliday: quota.bussUndBetTagHoliday,
      augsburgerFriedensfestHoliday: quota.augsburgerFriedensfestHoliday,
      christmasEveHoliday: quota.christmasEveHoliday,
      newYearsEveHoliday: quota.newYearsEveHoliday,
      easterSundayHoliday: quota.easterSundayHoliday,
      pfingstSundayHoliday: quota.pfingstSundayHoliday,
    });
    setIsEditMode(true);
    setEditQuotaId(quota.id);
  };

  const sortedQuotas = useMemo(() => {
    return [...vacationQuotas].sort((a, b) => {
      const startA = dayjs(a.validFrom).valueOf();
      const startB = dayjs(b.validFrom).valueOf();
      return startA - startB;
    });
  }, [vacationQuotas]);

  const handleCreateNextYear = async () => {
    if (!activeQuota) return;
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!absenceServiceModuleId) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.missingModule"));
      return;
    }

    if (!companyId) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.missingCompany"));
      return;
    }

    const validFrom = dayjs(activeQuota.validFrom).add(1, "year");
    const validUntil = dayjs(activeQuota.validUntil).add(1, "year");
    const surplusValidUntil = dayjs(activeQuota.surplusValidUntil).add(1, "year");

    const payload = {
      employeeId: employee.id,
      companyId,
      validFrom: validFrom.format("YYYY-MM-DD"),
      validUntil: validUntil.format("YYYY-MM-DD"),
      totalVacationDays: activeQuota.totalVacationDays,
      surplusFromPrevious: 0,
      surplusValidUntil: surplusValidUntil.format("YYYY-MM-DD"),
      isMondayWorkday: activeQuota.isMondayWorkday,
      isTuesdayWorkday: activeQuota.isTuesdayWorkday,
      isWednesdayWorkday: activeQuota.isWednesdayWorkday,
      isThursdayWorkday: activeQuota.isThursdayWorkday,
      isFridayWorkday: activeQuota.isFridayWorkday,
      isSaturdayWorkday: activeQuota.isSaturdayWorkday,
      isSundayWorkday: activeQuota.isSundayWorkday,
      holy3KingsDayHoliday: activeQuota.holy3KingsDayHoliday,
      internationalWomensHoliday: activeQuota.internationalWomensHoliday,
      fronleichnamHoliday: activeQuota.fronleichnamHoliday,
      mariaHimmelfahrtHoliday: activeQuota.mariaHimmelfahrtHoliday,
      worldChildrenDayHoliday: activeQuota.worldChildrenDayHoliday,
      reformationDayHoliday: activeQuota.reformationDayHoliday,
      allerheiligenDayHoliday: activeQuota.allerheiligenDayHoliday,
      bussUndBetTagHoliday: activeQuota.bussUndBetTagHoliday,
      augsburgerFriedensfestHoliday: activeQuota.augsburgerFriedensfestHoliday,
      christmasEveHoliday: activeQuota.christmasEveHoliday,
      newYearsEveHoliday: activeQuota.newYearsEveHoliday,
      easterSundayHoliday: activeQuota.easterSundayHoliday,
      pfingstSundayHoliday: activeQuota.pfingstSundayHoliday,
    };

    try {
      setIsSubmitting(true);
      const result = await absenceApi.createVacationQuota(
        absenceServiceModuleId,
        payload
      );
      if (!result.success()) {
        setErrorMessage(
          result.errorMessage ??
            t("employeesPage.vacationQuota.errors.generic")
        );
        return;
      }
      setSuccessMessage(t("employeesPage.vacationQuota.successNextYear"));
      onClose();
    } catch (error) {
      setErrorMessage(t("employeesPage.vacationQuota.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("employeesPage.vacationQuota.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          {vacationQuotaLoading && (
            <Typography variant="body2" color="textSecondary">
              {t("employeesPage.vacationQuota.loading")}
            </Typography>
          )}
          {!vacationQuotaLoading && activeQuota && (
            <Stack spacing={0.75}>
              <Typography variant="h6">
                {t("employeesPage.vacationQuota.activeTitle")}
              </Typography>
              <Typography variant="subtitle1">
                {t("employeesPage.vacationQuota.active", {
                  days: activeQuota.totalVacationDays,
                  from: dayjs(activeQuota.validFrom).format("DD.MM.YYYY"),
                  until: dayjs(activeQuota.validUntil).format("DD.MM.YYYY"),
                })}
              </Typography>
              <Typography variant="subtitle1">
                {t("employeesPage.vacationQuota.activeSurplus", {
                  days: activeQuota.surplusFromPrevious,
                  until: dayjs(activeQuota.surplusValidUntil).format("DD.MM.YYYY"),
                })}
              </Typography>
              {mode === "view" && nextYearQuota && (
                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                  {t("employeesPage.vacationQuota.nextYear", {
                    days: nextYearQuota.totalVacationDays,
                    from: dayjs(nextYearQuota.validFrom).format("DD.MM.YYYY"),
                    until: dayjs(nextYearQuota.validUntil).format("DD.MM.YYYY"),
                  })}
                </Typography>
              )}
            </Stack>
          )}
          {!vacationQuotaLoading && !activeQuota && (
            <Typography variant="body2" color="textSecondary">
              {t("employeesPage.vacationQuota.noActive")}
            </Typography>
          )}
          {(mode === "view" && vacationQuotas.length > 0) && (
            <Stack spacing={2}>
              <TextField
                select
                label={t("employeesPage.vacationQuota.editSelectLabel")}
                value={editQuotaId}
                onChange={(event) => {
                  const nextValue = event.target.value === ""
                    ? ""
                    : Number(event.target.value);
                  if (nextValue === "") {
                    setEditQuotaId("");
                    setIsEditMode(false);
                    return;
                  }
                  const selectedQuota = sortedQuotas.find((quota) => quota.id === nextValue);
                  if (selectedQuota) {
                    handleEditSelection(selectedQuota);
                  }
                }}
                fullWidth
              >
                <MenuItem value="">
                  {t("employeesPage.vacationQuota.editSelectPlaceholder")}
                </MenuItem>
                {sortedQuotas.map((quota) => (
                  <MenuItem key={quota.id} value={quota.id}>
                    {dayjs(quota.validFrom).format("DD.MM.YYYY")} - {dayjs(quota.validUntil).format("DD.MM.YYYY")}
                  </MenuItem>
                ))}
              </TextField>
              {activeQuota && (
                <Stack direction="row" sx={{ justifyContent: "center" }}>
                  <GeneralButton
                    appVariant="secondary"
                    onClick={handleCreateNextYear}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {t("employeesPage.vacationQuota.createNextYear")}
                  </GeneralButton>
                </Stack>
              )}
            </Stack>
          )}

          {(mode === "create" || isEditMode || isCreateOnly) && (
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {isEditMode
                  ? t("employeesPage.vacationQuota.editTitle")
                  : t("employeesPage.vacationQuota.createTitle")}
              </Typography>
              <TextField
                label={t("employeesPage.vacationQuota.vacationDays")}
                type="number"
                value={vacationDays}
                onChange={(event) => setVacationDays(event.target.value)}
                slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                fullWidth
              />
              <TextField
                label={t("employeesPage.vacationQuota.surplusDays")}
                type="number"
                value={surplusDays}
                onChange={(event) => setSurplusDays(event.target.value)}
                slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                fullWidth
              />

              <Stack direction="row" spacing={2}>
                <DatePicker
                  label={t("employeesPage.vacationQuota.validFrom")}
                  value={validFrom}
                  onChange={setValidFrom}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("employeesPage.vacationQuota.validUntil")}
                  value={validUntil}
                  onChange={setValidUntil}
                  minDate={validFrom ?? undefined}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>

              <DatePicker
                label={t("employeesPage.vacationQuota.surplusValidUntil")}
                value={surplusValidUntil}
                onChange={setSurplusValidUntil}
                minDate={validFrom ?? undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <Stack spacing={1}>
                <Typography variant="subtitle1">
                  {t("employeesPage.vacationQuota.workdays")}
                </Typography>
                <Stack direction="row" sx={{ flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={workdays.monday}
                        onChange={() => toggleWorkday("monday")}
                      />
                    }
                    label={t("employeesPage.vacationQuota.days.monday")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={workdays.tuesday}
                        onChange={() => toggleWorkday("tuesday")}
                      />
                    }
                    label={t("employeesPage.vacationQuota.days.tuesday")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={workdays.wednesday}
                        onChange={() => toggleWorkday("wednesday")}
                      />
                    }
                    label={t("employeesPage.vacationQuota.days.wednesday")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={workdays.thursday}
                        onChange={() => toggleWorkday("thursday")}
                      />
                    }
                    label={t("employeesPage.vacationQuota.days.thursday")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={workdays.friday}
                        onChange={() => toggleWorkday("friday")}
                      />
                    }
                    label={t("employeesPage.vacationQuota.days.friday")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={workdays.saturday}
                        onChange={() => toggleWorkday("saturday")}
                      />
                    }
                    label={t("employeesPage.vacationQuota.days.saturday")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={workdays.sunday}
                        onChange={() => toggleWorkday("sunday")}
                      />
                    }
                    label={t("employeesPage.vacationQuota.days.sunday")}
                  />
                </Stack>
              </Stack>
              <RegionalHolidays value={regionalHolidays} onUpdate={(v) => setRegionalHolidays(v)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <GeneralButton appVariant="secondary" onClick={onClose}>
          {t("buttons.cancel")}
        </GeneralButton>
        {(mode === "create" || isEditMode || isCreateOnly) && (
          <GeneralButton
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canEdit || isSubmitting}
          >
            {isEditMode ? t("employeesPage.vacationQuota.saveEdit") : t("buttons.save")}
          </GeneralButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VacationQuotaDialog;
